<template>
  <div
    class="v-preview-image"
    :class="{
      'with-text': !!text,
      [orientation]: orientation,
      [extraClass]: !!extraClass
    }"
  >
    <div
      class="image-wrapper"
      :class="{ [size]: size, ['no-title']: hideTitle }"
      @click="onClick"
    >
      <img :src="image || _imgDefaultSrc" alt="preview" />
      <div
        class="title"
        :class="{
          ['hide']: hideTitle,
          ['with-download']: withPdfDownload,
          ['loading-wrapper']: pdfLoading
        }"
      >
        <div
          class="ball"
          v-if="withPdfDownload"
          @click="pdfDownloadHandler"
        ></div>
        <span v-if="!hideTitle" :class="{ ['loading']: pdfLoading }">{{
          pdfLoading ? "Loading..." : title
        }}</span>
        <img
          v-if="withPdfDownload && !pdfLoading"
          :src="pdfDownload"
          alt="download-pdf"
          @click="pdfDownloadHandler"
        />
      </div>
    </div>

    <div class="preview-text" v-if="text || component">
      <VLink v-if="routerLink" :label="text" :routerLink="routerLink"></VLink>
      <component
        v-else-if="component"
        :is="component"
        v-bind="textComponentProps"
      />
      <span v-else>{{ text }}</span>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";

// components
import VLink from "@/components/VLink";

// mixins
import withNavigate from "@/mixins/withNavigate";

// assets
import mailingSvg from "@/assets/images/mailing.svg";
import lettersPreview from "@/assets/images/letters-preview.svg";
import postcardPreview from "@/assets/images/postcard-preview.svg";
import lettersPreviewBig from "@/assets/images/letters-preview-big.svg";
import postcardPreviewBig from "@/assets/images/postcards-preview-big.svg";

import pdfDownload from "@/assets/images/pdf-download.svg";

export default Vue.component("VPreviewImage", {
  props: {
    image: VueTypes.string,
    size: VueTypes.oneOf(["small", "big"]).def("small"),
    title: VueTypes.string,
    text: VueTypes.string,
    routerLink: VueTypes.string,
    onClickHandler: VueTypes.func,
    textComponentProps: VueTypes.any,
    component: VueTypes.any,
    withPdfDownload: VueTypes.bool.def(false),
    pdfDownloadHandler: VueTypes.func,
    pdfLoading: VueTypes.bool.def(false),
    orientation: VueTypes.oneOf(["vertical", "horizontal"]).def("horizontal"),
    extraClass: VueTypes.string.def(""),
    hideTitle: VueTypes.bool.def(false),
    defaultImageSrc: VueTypes.string
  },

  components: {
    VLink
  },

  mixins: [withNavigate],

  data() {
    return {
      mailingSvg,
      pdfDownload
    };
  },

  methods: {
    onClick(e) {
      e.stopPropagation();
      this.$emit("click");
      this.onClickHandler && this.onClickHandler(e);
    }
  },

  computed: {
    _imgDefaultSrc() {
      if (this.defaultImageSrc) {
        return this.defaultImageSrc;
      } else if (this.size === "big") {
        return this.orientation === "vertical"
          ? lettersPreviewBig
          : postcardPreviewBig;
      }

      return this.orientation === "vertical" ? lettersPreview : postcardPreview;
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-preview-image";

.#{$ns} {
  display: flex;
  align-items: center;
  padding-left: 7px;

  .preview-text {
    padding: 0 30px;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;

    /deep/ a {
      color: $secondary-text-color;

      &:hover {
        color: $primary-color;
      }
    }
  }
}

.image-wrapper {
  position: relative;
  box-shadow: 2px 2px 12px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
  border-radius: 5px;

  &.small {
    width: 70px;
    height: 50px;
  }

  /*for big preview image*/
  &.big {
    width: 340px;
    height: 220px;
    box-shadow: 2px 2px 25px rgba(0, 0, 0, 0.15);
    pointer-events: none;

    .title {
      .ball {
        height: 125px;
        width: 125px;
        top: 75%;
        right: -40%;
      }

      img {
        width: 25px;
        height: 25px;
        bottom: 10px;
        right: 10px;
      }
    }
  }
  /*-------------*/

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 5px;
  }

  &:hover {
    &:not(.no-title) {
      box-shadow: none;
    }

    .title {
      opacity: 1;
    }
  }
}

.title {
  overflow: hidden;

  span {
    @include textCropEllipsis();
    padding: 8px;
  }

  .ball {
    cursor: pointer;
    position: absolute;
    width: 65px;
    height: 63px;
    border-radius: 50%;
    background: white;
    top: 50%;
    right: -50%;
    transform: translate(-35%, -18%);
  }

  transition: opacity 0.1s linear;
  opacity: 0;
  position: absolute;
  background: white;
  width: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: 3px;
  color: $secondary-text-color;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.17);

  &.hide {
    box-shadow: none;
    cursor: default;
    background: transparent;
  }

  &.with-download {
    background: transparent;
    z-index: 1;
    height: 100%;
    border-radius: 5px;
    border: 1px solid $primary-color;

    &.loading-wrapper {
      background: rgba(255, 255, 255, 0.8);

      span {
        font-family: Lato-Bold, sans-serif;
      }

      .ball {
        display: none;
      }
    }

    span {
      position: absolute;
      top: 5px;
      left: 5px;
      color: $secondary-text-color;
      padding: 0;
      max-width: 90%;
      font-size: 12px;
      line-height: 1.2em;
      @include textCropEllipsis();

      &.loading {
        color: $primary-color;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }

    img {
      cursor: pointer;
      position: absolute;
      bottom: 5px;
      right: 5px;
      width: 20px;
      height: 20px;
    }
  }
}

.#{$ns}.vertical {
  .small {
    width: 40px;
    height: 50px;
    max-width: 40px;
    min-height: 55px;
  }

  .title {
    .ball {
      transform: translate(10%, 0);
      right: auto;
      left: 15%;
    }
  }

  .with-download img {
    bottom: 5px;
    right: 5px;
  }

  .big {
    width: 160px;
    height: 220px;
    max-width: 160px;
    min-width: 160px;

    pointer-events: none;
  }

  .title {
    .ball {
      transform: translate(10%, 0);
      right: auto;
      left: 55%;
    }
  }

  .with-download img {
    bottom: 5px;
    right: 5px;
  }
}
</style>
